<template>
  <div class="message-box dialog-mask">
    <div class="dialog-content" role="dialog" aria-modal="true" aria-labelledby="yes-no-dialog-title">
      <header class="dialog-header">
        <h2 id="yes-no-dialog-title" class="dialog-title">{{ dialogTitle }}</h2>
        <button type="button" class="close-icon-btn" :aria-label="closeAriaLabel" @click="$close(false)">
          <IconClose />
        </button>
      </header>
      <div class="dialog-body">
        <div v-html="content"></div>
      </div>
      <footer>
        <div class="button-area">
          <button type="button" class="button" :class="okVariant" @click="$close(true)">
            {{ okButton || $t("common.yes") }}
          </button>
          <button type="button" class="button cancel" @click="$close(false)">
            {{ cancelButton || $t("common.cancel") }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import IconClose from "~icons/figma/Close";

export default Vue.extend({
  components: { IconClose },
  props: {
    title: String,
    content: String,
    okButton: {
      type: String,
      default: undefined,
    },
    cancelButton: {
      type: String,
      default: undefined,
    },
    okVariant: {
      type: String,
      default: "danger",
      validator: (value: string) => ["danger", "primary"].includes(value),
    },
  },
  computed: {
    dialogTitle(): string {
      return this.title || (this.$t("common.confirmTitle") as string);
    },
    closeAriaLabel(): string {
      return this.$t("common.close") as string;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.dialog-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 16px;
}

.dialog-title {
  margin: 0;
  padding: 0 28px;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-lg);
  text-align: center;
  color: var(--color-text-normal);
}

footer {
  width: 100%;
  margin-top: auto;
}

.button-area {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin: 16px 0 0;
  gap: 8px;
}

.close-icon-btn {
  position: absolute;
  top: 50%;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--color-text-low);
  cursor: pointer;
  transform: translateY(-50%);

  svg {
    width: 16px;
    height: 16px;
  }

  @include hover-overlay;

  &:hover {
    color: var(--color-text-normal);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.button {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 0;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: filter 0.2s ease;
  margin: 0;

  &.cancel {
    background-color: var(--color-surface-muted);
    color: var(--color-text-normal);
    border: none;

    @include hover-overlay;
  }

  &.danger {
    background-color: var(--color-warning);
    color: var(--color-text-on-fixed-bg);
    border: none;

    @include hover-overlay;
  }

  &.primary {
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
    border: none;

    @include hover-overlay;
  }
}

.fade-enter-active {
  transition: opacity 0.3s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.dialog-body {
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}

.dialog-mask {
  position: fixed;
  top: -100px;
  right: 0;
  bottom: 0;
  left: 0;
  display: -ms-flexbox;
  display: flex;
  z-index: 9000;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-content {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 400px;
  max-width: calc(100% - 32px);
  margin: 4px 0;
  padding: 16px;
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}
</style>
